import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/errors/use-toast';
import { updateUser } from '@/hooks/auth/database-users';
import { LightboxImage } from '@/components/image-lightbox';
import { ProfileAvatarActions } from './types';

interface UseProfileAvatarActionsProps {
  state: any;
  setState: any;
  user: any;
  profilePictureUrl?: string | null;
  onUploadClick?: () => void;
  images: any[];
  deleteImage: (id: string, isCurrentProfile: boolean) => Promise<void>;
  isDeleting: boolean;
}

export const useProfileAvatarActions = ({
  state,
  setState,
  user,
  profilePictureUrl,
  onUploadClick,
  images,
  deleteImage,
  isDeleting
}: UseProfileAvatarActionsProps): ProfileAvatarActions => {
  const queryClient = useQueryClient();

  const handleProfilePictureClick = () => {
    if (profilePictureUrl) {
      setState((prev: any) => ({ ...prev, isLightboxOpen: true }));
    } else if (onUploadClick) {
      onUploadClick();
    }
  };

  const handleChooseProfilePicture = async () => {
    if (!state.currentImage || !user?.id) return;
    
    try {
      setState((prev: any) => ({ ...prev, isSettingProfilePic: true }));
      
      const { error } = await updateUser(user.id, {
        profile_picture_url: state.currentImage.url
      });
      
      if (error) throw error;
      
      setState((prev: any) => ({ ...prev, isLightboxOpen: false }));
      
      // Invalidate the new user profile query
      queryClient.invalidateQueries({ queryKey: ['user-profile', user.id] });
      queryClient.refetchQueries({ queryKey: ['user-profile', user.id] });
      
      const event = new CustomEvent('profile-picture-updated', {
        detail: { url: state.currentImage.url }
      });
      document.dispatchEvent(event);
      
      toast({
        title: "Profile picture updated",
        description: "Your profile picture has been updated successfully.",
        variant: "success"
      });
    } catch (error) {
      console.error('Error setting profile picture:', error);
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update profile picture",
        variant: "error"
      });
    } finally {
      setState((prev: any) => ({ ...prev, isSettingProfilePic: false }));
    }
  };

  const handleDeleteClick = () => {
    setState((prev: any) => ({ ...prev, showDeleteConfirmation: true }));
  };

  const handleConfirmDelete = async () => {
    if (!state.currentImage || !user?.id) return;
    
    try {
      const isProfilePicture = state.currentImage.url === profilePictureUrl;
      
      // Find the next available image before deletion
      let nextImageUrl = null;
      let nextImage = null;
      
      if (isProfilePicture && images.length > 1) {
        const currentImageIndex = images.findIndex(img => img.url === state.currentImage.url);
        const remainingImages = images.filter((_, index) => index !== currentImageIndex);
        if (remainingImages.length > 0) {
          nextImageUrl = remainingImages[0].url;
          nextImage = {
            id: remainingImages[0].id,
            url: remainingImages[0].url,
            alt: 'Profile Picture'
          };
        }
      }
      
      // If this is the current profile picture, update to the next one first
      if (isProfilePicture && nextImageUrl) {
        console.log('Updating profile picture to next image:', nextImageUrl);
        
        const { error: updateError } = await updateUser(user.id, {
          profile_picture_url: nextImageUrl
        });
        
        if (updateError) throw updateError;
        
        // Update local state immediately to prevent empty state
        setState((prev: any) => ({ 
          ...prev, 
          currentImage: nextImage
        }));
        
        // Wait for queries to update
        await queryClient.invalidateQueries({ queryKey: ['user-profile', user.id] });
        await queryClient.refetchQueries({ queryKey: ['user-profile', user.id] });
        
        const event = new CustomEvent('profile-picture-updated', {
          detail: { url: nextImageUrl }
        });
        document.dispatchEvent(event);
      }
      
      // Now delete the image
      const imageToDelete = images.find(img => img.url === state.currentImage.url);
      if (imageToDelete) {
        await deleteImage(imageToDelete.id, isProfilePicture);
        
        // If we deleted the current profile picture and there are no more images
        if (isProfilePicture && !nextImageUrl) {
          const { error: clearError } = await updateUser(user.id, {
            profile_picture_url: null
          });
          
          if (clearError) throw clearError;
          
          // Invalidate queries to reflect the cleared profile picture
          await queryClient.invalidateQueries({ queryKey: ['user-profile', user.id] });
          await queryClient.refetchQueries({ queryKey: ['user-profile', user.id] });
          
          const event = new CustomEvent('profile-picture-updated', {
            detail: { url: null }
          });
          document.dispatchEvent(event);
        }
        
        setState((prev: any) => ({ ...prev, showDeleteConfirmation: false }));
        
        toast({
          title: "Image deleted",
          description: "The image has been deleted successfully.",
          variant: "success"
        });
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete image",
        variant: "error"
      });
    }
  };

  const handleCancelDelete = () => {
    setState((prev: any) => ({ ...prev, showDeleteConfirmation: false }));
  };

  const setIsLightboxOpen = (isOpen: boolean) => {
    setState((prev: any) => ({ ...prev, isLightboxOpen: isOpen }));
  };

  const handleNavigate = (direction: 'next' | 'previous') => {
    if (!images.length) return;
    
    const currentIndex = images.findIndex(img => img.url === state.currentImage?.url);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    }
    
    const newImage = images[newIndex];
    const newCurrentImage = {
      id: newImage.id,
      url: newImage.url,
      alt: 'Profile Picture'
    };
    
    setState((prev: any) => ({
      ...prev,
      currentImage: newCurrentImage,
      isCurrentImageActiveProfile: newImage.url === profilePictureUrl
    }));
  };

  return {
    handleProfilePictureClick,
    handleChooseProfilePicture,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    setIsLightboxOpen,
    handleNavigate
  };
};
